import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { errorText } from '../utils/errorText';
import { trapTab } from '../utils/focusTrap';
import { reportTab } from '../utils/issueReports';
import { focusElement } from '../utils/useFocusOnOpen';
import { startViewAs } from '../utils/viewAs';

/*
 * "View the app as…": the roles this administrator may view as (GET
 * /auth/view-as/roles, the roles they could grant, never public), one chosen,
 * then a fresh page under the borrowed token (utils/viewAs.js). The server
 * makes it read-only and ends it within the hour; the banner says so and
 * holds Exit (components/ViewAsBanner.js).
 *
 * A labelled modal dialog like the Report issue one: focus on its heading,
 * Tab kept inside, Escape and × close it except while starting, focus back to
 * the opener.
 */
export default function ViewAsDialog({ open, onClose, navigate = (path) => window.location.assign(path) }) {
  if (!open) return null;
  const dialog = <Dialog onClose={onClose} navigate={navigate} />;
  return typeof document !== 'undefined' && document.body ? createPortal(dialog, document.body) : dialog;
}

function Dialog({ onClose, navigate }) {
  const { t } = useTranslation(['viewAs', 'users']);
  const [roles, setRoles] = useState(null);          // null while loading
  const [maxMinutes, setMaxMinutes] = useState(60);
  const [chosen, setChosen] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const dialogRef = useRef(null);
  const headingRef = useRef(null);
  const opener = useRef(null);
  const busyRef = useRef(false);
  busyRef.current = busy;
  const closeRef = useRef(onClose);
  useEffect(() => { closeRef.current = onClose; });

  useEffect(() => {
    let live = true;
    api.get('/auth/view-as/roles')
      .then((res) => {
        if (!live) return;
        setRoles(Array.isArray(res?.data?.roles) ? res.data.roles : []);
        if (Number.isFinite(res?.data?.max_minutes)) setMaxMinutes(res.data.max_minutes);
      })
      .catch((e) => {
        if (!live) return;
        setRoles([]);
        setError(t('loadFailed', { error: errorText(e) }));
      });
    return () => { live = false; };
  }, [t]);

  useEffect(() => {
    opener.current = typeof document !== 'undefined' ? document.activeElement : null;
    focusElement(headingRef.current);
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (!busyRef.current) closeRef.current?.();
        return;
      }
      trapTab(dialogRef.current, e, { extra: reportTab() });
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      const back = opener.current;
      if (back && typeof back.focus === 'function' && back.isConnected !== false) back.focus({ preventScroll: true });
    };
  }, []);

  const close = () => { if (!busy) onClose?.(); };

  const start = async (e) => {
    e.preventDefault();
    if (!chosen || busy) return;
    setBusy(true);
    setError('');
    try {
      await startViewAs(chosen, { post: (url, body) => api.post(url, body) });
      // A fresh page: nothing read as the administrator stays on screen.
      navigate('/');
    } catch (err) {
      setBusy(false);
      setError(t('startFailed', { error: errorText(err) }));
    }
  };

  return (
    <div className="hub-drawer-backdrop" data-testid="view-as-backdrop" style={{ zIndex: 3000 }}>
      <aside ref={dialogRef} className="hub-drawer" role="dialog" aria-modal="true" aria-labelledby="view-as-title"
        aria-busy={busy} onClick={(e) => e.stopPropagation()} data-testid="view-as-dialog"
        style={{ zIndex: 3001, whiteSpace: 'normal' }}>
        <div className="hub-drawer__head" style={{ marginBottom: 12 }}>
          <h3 id="view-as-title" ref={headingRef} tabIndex={-1} style={{ margin: 0, fontSize: 17 }}>{t('title')}</h3>
          <button type="button" onClick={close} disabled={busy} aria-label={t('close')} title={t('close')}
            style={{ background: 'none', border: 'none', fontSize: 22, cursor: busy ? 'not-allowed' : 'pointer',
              color: 'var(--c-text-muted-fg)' }}>×</button>
        </div>
        <p style={{ marginTop: 0, color: 'var(--c-text-muted-fg)', fontSize: 14 }}>{t('intro', { minutes: maxMinutes })}</p>
        <p style={{ color: 'var(--c-text-muted-fg)', fontSize: 13 }}>{t('scopeNote')}</p>

        <form onSubmit={start}>
          {roles === null && <p role="status" style={{ fontSize: 14 }}>{t('loading')}</p>}
          {roles && roles.length === 0 && !error && <p style={{ fontSize: 14 }}>{t('none')}</p>}
          {roles && roles.length > 0 && (
            <fieldset style={{ border: 'none', padding: 0, margin: '0 0 12px' }}>
              <legend style={{ fontWeight: 600, fontSize: 14, marginBottom: 6 }}>{t('roleLabel')}</legend>
              {roles.map((role) => (
                <label key={role} style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 36, fontSize: 15,
                  cursor: busy ? 'default' : 'pointer' }}>
                  <input type="radio" name="view-as-role" value={role} checked={chosen === role}
                    disabled={busy} onChange={() => setChosen(role)} data-testid={`view-as-role-${role}`} />
                  <span>{t(`users:role.${role}`, { defaultValue: role })}</span>
                </label>
              ))}
            </fieldset>
          )}
          {error && <p role="alert" style={{ color: 'var(--c-danger-fg)', fontSize: 14 }}>{error}</p>}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button type="submit" disabled={!chosen || busy} data-testid="view-as-start"
              style={{ background: 'var(--c-primary)', color: 'var(--c-on-accent)', border: 'none', borderRadius: 6,
                padding: '9px 16px', fontSize: 14, cursor: !chosen || busy ? 'not-allowed' : 'pointer',
                opacity: !chosen || busy ? 0.6 : 1 }}>
              {busy ? t('starting') : t('start')}
            </button>
            <button type="button" onClick={close} disabled={busy}
              style={{ background: 'var(--c-surface)', color: 'var(--c-text-fg)', border: '1px solid var(--c-border)',
                borderRadius: 6, padding: '9px 16px', fontSize: 14, cursor: busy ? 'not-allowed' : 'pointer' }}>
              {t('cancel')}
            </button>
          </div>
        </form>
      </aside>
    </div>
  );
}
